import React, { useRef } from "react";
import { motion, useMotionValue, useSpring, useTransform } from "framer-motion";

/**
 * Premium magnetic CTA button. Two variants: primary (white) / secondary (ghost).
 * Use as a button or anchor (asChild via "as" prop).
 */
export const MagneticButton = ({
  children,
  variant = "primary",
  onClick,
  href,
  className = "",
  "data-testid": testId,
  type = "button",
  disabled,
}) => {
  const ref = useRef(null);
  const mx = useMotionValue(0);
  const my = useMotionValue(0);
  const x = useSpring(mx, { stiffness: 220, damping: 18, mass: 0.4 });
  const y = useSpring(my, { stiffness: 220, damping: 18, mass: 0.4 });

  const tx = useTransform(x, (v) => v * 0.25);
  const ty = useTransform(y, (v) => v * 0.25);

  const handleMove = (e) => {
    const el = ref.current;
    if (!el) return;
    const rect = el.getBoundingClientRect();
    const cx = rect.left + rect.width / 2;
    const cy = rect.top + rect.height / 2;
    mx.set(e.clientX - cx);
    my.set(e.clientY - cy);
  };

  const handleLeave = () => {
    mx.set(0);
    my.set(0);
  };

  const baseClasses =
    "group relative inline-flex items-center justify-center gap-3 px-7 py-4 text-[12px] tracking-[0.22em] uppercase font-medium select-none whitespace-nowrap transition-colors duration-300";

  const variantClasses =
    variant === "primary"
      ? "bg-white text-black hover:bg-zinc-200"
      : "bg-transparent text-white border border-white/25 hover:border-white/70";

  const content = (
    <motion.span
      style={{ x: tx, y: ty }}
      className="relative inline-flex items-center gap-2"
    >
      {children}
      <span aria-hidden className="inline-block transition-transform duration-300 group-hover:translate-x-1">
        →
      </span>
    </motion.span>
  );

  const commonProps = {
    ref,
    onMouseMove: handleMove,
    onMouseLeave: handleLeave,
    className: `${baseClasses} ${variantClasses} ${className}`,
    "data-testid": testId,
  };

  if (href) {
    return (
      <a href={href} onClick={onClick} {...commonProps}>
        {content}
      </a>
    );
  }

  return (
    <button type={type} disabled={disabled} onClick={onClick} {...commonProps}>
      {content}
    </button>
  );
};

export default MagneticButton;
